// A scene's fog of war: rows keyed like terrain (one string per row of
// cells, "." for the scene's default, "h" hidden, "r" revealed), twin of
// MapFog and MapScene::FOG_FIELDS. Squares and hexes both take fog, unlike
// terrain.
import type { LiveScene, LiveToken } from "../types/generated/live";
import { EMPTY, MAX_CELLS, paintable, paintCells } from "./terrain";

export type FogState = "hidden" | "revealed";
export type Fog = { enabled?: boolean; default?: FogState; rows?: string[] };
type Cell = { col: number; row: number };
type Point = { x: number; y: number };
type Rect = { x: number; y: number; width: number; height: number };
type Scene = Pick<LiveScene, "width_px" | "height_px">;
type GridMath = {
	centerOf: (col: number, row: number) => Point;
	cellAt: (x: number, y: number) => Cell;
	visibleCells: (rect: Rect) => Cell[];
};
// What a fog needs of a token, all of it in the players' view (MapPlayersView::TOKEN_KEYS).
type Token = Pick<LiveToken, "kind" | "x" | "y">;

export function fogRows(fog?: Fog | null): string[] {
	return Array.isArray(fog?.rows) ? [...fog.rows] : [];
}

function defaultState(fog?: Fog | null): FogState {
	return fog?.default === "revealed" ? "revealed" : "hidden";
}

// Solves the affine coordinate (x for columns, y for rows) for the integer
// range that lands inside [0, size], within storage's [0, MAX_CELLS), the
// cells terrain.paintable accepts. Solving can miss a centre that rounding
// puts exactly on an edge, so the painter's own comparisons settle each end.
function axisRange(at: (index: number) => number, size: number): [number, number] {
	const c0 = at(0);
	const slope = at(1) - c0;
	const last = MAX_CELLS - 1;
	let min = Math.min(last, Math.max(0, Math.ceil((0 - c0) / slope)));
	while (min > 0 && at(min - 1) >= 0) min--;
	while (min < last && at(min) < 0) min++;
	let max = Math.min(last, Math.max(min, Math.floor((size - c0) / slope)));
	while (max < last && at(max + 1) <= size) max++;
	while (max > min && at(max) > size) max--;
	return [min, max];
}

// The column into the paintable columns, then the row into that column's
// paintable rows, so a half-cell at a book map's edge or a piece in the
// margin shares its neighbour's state (twin of MapFog#state_at's clamp).
export function clampToPaintable(math: GridMath, scene: Scene, col: number, row: number): Cell {
	const [colMin, colMax] = axisRange((c) => math.centerOf(c, 0).x, scene.width_px);
	const clampedCol = Math.min(Math.max(col, colMin), colMax);
	const [rowMin, rowMax] = axisRange((r) => math.centerOf(clampedCol, r).y, scene.height_px);
	return { col: clampedCol, row: Math.min(Math.max(row, rowMin), rowMax) };
}

export function stateAt(math: GridMath, scene: Scene, fog: Fog | null | undefined, col: number, row: number): FogState {
	const at = clampToPaintable(math, scene, col, row);
	const code = fogRows(fog)[at.row]?.[at.col];
	if (code === "h") return "hidden";
	if (code === "r") return "revealed";
	return defaultState(fog);
}

// A disabled fog hides nothing.
export function hiddenAt(math: GridMath, scene: Scene, fog: Fog | null | undefined, x: number, y: number): boolean {
	if (!fog?.enabled) return false;
	const { col, row } = math.cellAt(x, y);
	return stateAt(math, scene, fog, col, row) === "hidden";
}

// The party (sheet tokens) is never hidden by fog.
export function isFogged(token: Token, fog: Fog | null | undefined, math: GridMath, scene: Scene): boolean {
	if (token.kind === "sheet") return false;
	return hiddenAt(math, scene, fog, token.x, token.y);
}

// One code for every cell of a stroke or a rectangle: "." when the target
// state is already the default, so one fog has one spelling.
export function paintFog(rows: string[], cells: Cell[], state: FogState, fogDefault?: FogState): string[] {
	const code = state === defaultState({ default: fogDefault }) ? EMPTY : state === "revealed" ? "r" : "h";
	return paintCells(rows, cells, code);
}

// The cells whose centre lies inside the rectangle, in either drag
// direction; a zero-size rectangle is the one cell under the point.
export function cellsInRect(math: GridMath, scene: Scene, rect: Rect): Cell[] {
	const x0 = Math.min(rect.x, rect.x + rect.width);
	const x1 = Math.max(rect.x, rect.x + rect.width);
	const y0 = Math.min(rect.y, rect.y + rect.height);
	const y1 = Math.max(rect.y, rect.y + rect.height);
	if (x0 === x1 && y0 === y1) {
		const cell = math.cellAt(rect.x, rect.y);
		return paintable(math, scene, cell) ? [cell] : [];
	}
	const cells: Cell[] = [];
	for (const cell of math.visibleCells({ x: x0, y: y0, width: x1 - x0, height: y1 - y0 })) {
		if (!paintable(math, scene, cell)) continue;
		const { x, y } = math.centerOf(cell.col, cell.row);
		if (x >= x0 && x <= x1 && y >= y0 && y <= y1) cells.push(cell);
	}
	return cells;
}

// Whether two lists hold the same cells in the same order, which is how
// cellsInRect lists one set of cells.
export function sameCells(a: Cell[], b: Cell[]): boolean {
	return a.length === b.length && a.every((cell, index) => cell.col === b[index].col && cell.row === b[index].row);
}

// Trailing "." per row and trailing empty rows go, so one painting has one
// spelling (twin of MapFog.normalize_rows).
export function normalizeRows(rows: string[] | null | undefined): string[] {
	const trimmed = (Array.isArray(rows) ? rows : []).map((line) => String(line).replace(/\.+$/, ""));
	while (trimmed.length && trimmed.at(-1) === "") trimmed.pop();
	return trimmed;
}
